import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FunctionalButton from "components/layout/FunctionalButton";

const AUTO_DISMISS_MS = 3000;

/** NavigateStairs.action feedback: show only current FSM state (snake/camel from roslib). */
const getFeedbackCurrentState = (fb) => {
    if (!fb || typeof fb !== "object") return null;
    const s = fb.current_state ?? fb.currentState;
    if (s == null) return null;
    const t = String(s).trim();
    return t || null;
};

const getTerminalOutcomeLabel = (status, result) => {
    if (status === "succeeded") {
        if (result && typeof result.success === "boolean" && !result.success) return "Fail";
        return "Success";
    }
    if (status === "failed") return "Fail";
    if (status === "canceled") return "Canceled";
    return null;
};

const cancelButtonClassName =
    "shrink-0 rounded-r5 border border-red-600 bg-red-600 px-3 py-1 text-h5 text-white hover:border-red-500 hover:bg-red-500";

const StairActionFeedback = ({ visible, actionClient, goalId, feedback, result, status, isActionValid, onCancel, onClose, onForceClose }) => {
    const { t } = useTranslation("control");
    const [cancelError, setCancelError] = useState(null);

    const handleCancel = () => {
        console.log("[StairActionFeedback] Cancel button clicked");
        console.log("[StairActionFeedback] onCancel:", onCancel, "actionClient:", actionClient, "goalId:", goalId);

        if (onCancel) {
            try {
                console.log("[StairActionFeedback] Calling onCancel callback");
                onCancel();
                setCancelError(null);
            } catch (err) {
                console.error("[StairActionFeedback] Failed to cancel action:", err);
                setCancelError(`Failed to cancel: ${err.message || err}`);
            }
        } else if (actionClient && goalId) {
            try {
                console.log("[StairActionFeedback] Calling cancelGoal directly");
                actionClient.cancelGoal(goalId);
                setCancelError(null);
            } catch (err) {
                console.error("[StairActionFeedback] Failed to cancel action:", err);
                setCancelError(`Failed to cancel: ${err.message || err}`);
            }
        } else {
            console.error("[StairActionFeedback] Cannot cancel: missing onCancel, actionClient, or goalId");
            setCancelError("Cannot cancel: missing required data");
        }
    };

    const showClose = status === "succeeded" || status === "failed" || status === "canceled" || !isActionValid;
    const showForceClose = !isActionValid && status !== "active";
    const autoDismiss = showClose && !showForceClose && status !== "active";

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!visible || !autoDismiss) return;
        const timeout = setTimeout(() => onCloseRef.current?.(), AUTO_DISMISS_MS);
        return () => clearTimeout(timeout);
    }, [visible, autoDismiss, status]);

    if (!visible) return null;

    const stateLine =
        status === "active"
            ? getFeedbackCurrentState(feedback) ?? "Running"
            : getTerminalOutcomeLabel(status, result) ?? "—";

    return (
        <div
            className="pointer-events-auto absolute left-1/2 top-2 z-[100] flex max-h-[calc(100dvh-0.5rem-17rem)] max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-col gap-2 rounded-r10 border border-black80 bg-black90 px-4 py-2 shadow-lg"
            role="status"
        >
            {autoDismiss ? (
                <div className="flex min-h-0 items-center justify-center">
                    <span className="text-h5 text-center">{stateLine}</span>
                </div>
            ) : (
                <div className="flex min-h-0 items-center gap-3">
                    <span className="max-w-[calc(100vw-6rem)] truncate text-h5">{stateLine}</span>
                    <div className="flex shrink-0 items-center gap-2">
                        {status === "active" && (
                            <FunctionalButton title={t("feedback.cancel")} className={cancelButtonClassName} onClick={handleCancel} />
                        )}
                        {showForceClose && (
                            <FunctionalButton title={t("feedback.forceClose")} className={cancelButtonClassName} onClick={onForceClose} />
                        )}
                        {showClose && (
                            <FunctionalButton
                                title={t("feedback.close")}
                                className="shrink-0 rounded-r5 border border-black60 bg-black60 px-3 py-1 text-h5 text-white hover:border-black40 hover:bg-black70"
                                onClick={onClose}
                            />
                        )}
                    </div>
                </div>
            )}

            {cancelError && <div className="text-h5 text-warning">{cancelError}</div>}

            {!isActionValid && status === "active" && (
                <div className="text-h5 text-warning">Connection or action server may be unavailable.</div>
            )}
        </div>
    );
};

export default StairActionFeedback;
